import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, Button, Paper, Alert, Badge, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import ConsumerDetails from "./ConsumerDetails";
import ChatInterface from "./ChatInterface";
import { useMessagesApi } from "../hooks/useMessagesApi";
import { useConsumerApi } from "../hooks/useConsumerApi";
import { useConversationsApi } from "../hooks/useConversationsApi";
import useNotification from "../hooks/useNotification";
import { useSignalRContext } from "../contextProviders/SignalRProvider";

const MessageReply = ({ conversation, consumerCredit, onBack, unreadConversationCount = 0, existingUnreadConversationIds = [] }) => {
  const { loading, getConversationMessages, getPaginatedMessages } = useMessagesApi();
  const { getConsumerById, getCreditBalance } = useConsumerApi();
  const { unlockConversation, getLockSettings } = useConversationsApi();
  const { showNotification } = useNotification();
  const [allMessages, setAllMessages] = useState([]);
  const [consumerDetails, setConsumerDetails] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [lockDurationMs, setLockDurationMs] = useState(5 * 60 * 1000); // Default 5 minutes (consumer timer), will be updated from API
  const [operatorTimeoutMs, setOperatorTimeoutMs] = useState(10 * 60 * 1000); // Default 10 minutes (operator timer), will be updated from API
  const [lockExpiry, setLockExpiry] = useState(null); // Will be set after fetching lock settings
  const [operatorTimerActive, setOperatorTimerActive] = useState(false); // Track which timer is active (false = consumer, true = operator)
  const [isUnlocking, setIsUnlocking] = useState(false); // Loading state for back button
  const [showTimeoutModal, setShowTimeoutModal] = useState(false); // Show timeout explanation modal
  // Real-time unread conversation count - starts with prop value, updates via SignalR
  const [localUnreadConversationCount, setLocalUnreadConversationCount] = useState(unreadConversationCount);
  // Pagination state for initial load
  const [initialPaginationInfo, setInitialPaginationInfo] = useState({ totalCount: 0, hasMore: false });
  // Track conversations that were already in the unread queue when entering this screen
  // Pre-populated with existing IDs so they don't get counted as "new" when they receive messages
  const newConversationsRef = useRef(new Set(existingUnreadConversationIds));
  const timerRef = useRef(null);
  const hasTimedOutRef = useRef(false);
  const isUnmountingRef = useRef(false); // Track if component is unmounting
  const lockDurationMsRef = useRef(5 * 60 * 1000); // Ref to store consumer lock duration for callbacks
  const operatorTimeoutMsRef = useRef(10 * 60 * 1000); // Ref to store operator timeout for callbacks
  const operatorTimerActiveRef = useRef(false); // Ref to track which timer is active
  const lastProcessedMessageIdRef = useRef(null); // Deduplication: prevent processing same message multiple times
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  // Get SignalR context
  const signalR = useSignalRContext();

  // Sync local count with prop value when it changes
  useEffect(() => {
    setLocalUnreadConversationCount(unreadConversationCount);
  }, [unreadConversationCount]);

  // Listen for new messages in OTHER conversations to update unread conversation count
  useEffect(() => {
    const handleNewConversationMessage = (event) => {
      const message = event.detail;
      const messageConvId = message.conversationId || message.ConversationId;
      
      // Skip if this is for the CURRENT conversation (we're viewing it)
      if (messageConvId === conversation?.id) {
        return;
      }
      
      // Check if this is a NEW conversation we haven't counted yet
      // This increments count when a new conversation receives a message while we're on reply screen
      if (!newConversationsRef.current.has(messageConvId)) {
        newConversationsRef.current.add(messageConvId);
        console.log(`[MessageReply] 📩 New unread conversation detected: ${messageConvId}`);
        setLocalUnreadConversationCount(prev => {
          const newCount = prev + 1;
          console.log(`[MessageReply] Unread conversation count: ${prev} -> ${newCount}`);
          return newCount;
        });
      }
    };

    window.addEventListener("signalr-new-message-notification", handleNewConversationMessage);
    console.log(`[MessageReply] 🎧 Listening for new unread conversations`);
    
    return () => {
      window.removeEventListener("signalr-new-message-notification", handleNewConversationMessage);
    };
  }, [conversation?.id]);

  // Fetch lock settings from API on mount
  useEffect(() => {
    const fetchLockSettings = async () => {
      const settings = await getLockSettings();
      const durationMs = settings?.lockDurationMs || 5 * 60 * 1000;
      const operatorMs = settings?.operatorTimeoutMs || 10 * 60 * 1000;
      setLockDurationMs(durationMs);
      lockDurationMsRef.current = durationMs;
      setOperatorTimeoutMs(operatorMs);
      operatorTimeoutMsRef.current = operatorMs;
      // Initialize lock expiry with consumer timer (operator hasn't replied yet)
      setLockExpiry(Date.now() + durationMs);
    };
    fetchLockSettings();
  }, [getLockSettings]);
  
  // Store latest values in refs to avoid dependency issues
  const onBackRef = useRef(onBack);
  const conversationIdRef = useRef(conversation?.id);
  const unlockConversationRef = useRef(unlockConversation);
  const showNotificationRef = useRef(showNotification);

  // Update refs when values change
  useEffect(() => {
    onBackRef.current = onBack;
    conversationIdRef.current = conversation?.id;
    unlockConversationRef.current = unlockConversation;
    showNotificationRef.current = showNotification;
  });

  // Format time remaining
  const formatTime = useCallback((ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, []);

  // Timer countdown effect
  useEffect(() => {
    if (!lockExpiry) {
      return;
    }

    hasTimedOutRef.current = false;

    const updateTimer = () => {
      const remaining = lockExpiry - Date.now();
      if (remaining <= 0) {
        // Time expired - show timeout modal instead of immediately navigating back
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        if (!hasTimedOutRef.current) {
          hasTimedOutRef.current = true;
          // Unlock the conversation immediately
          unlockConversationRef.current(conversationIdRef.current);
          // Show the modal to explain why they're being redirected
          setShowTimeoutModal(true);
          // Auto-redirect after 5 seconds if user doesn't click OK
          setTimeout(() => {
            onBackRef.current();
          }, 10000);
        }
      } else {
        setTimeRemaining(remaining);
      }
    };

    // Update immediately
    updateTimer();

    // Update every second
    timerRef.current = setInterval(updateTimer, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [lockExpiry]); // Only lockExpiry as dependency

  // Listen for new inbound messages to reset timer
  useEffect(() => {
    const handleNewMessage = (event) => {
      const message = event.detail;
      const messageDirection = message.direction || message.Direction;
      const messageConvId = message.conversationId || message.ConversationId;
      
      // If inbound message for this conversation, switch to/reset consumer timer
      if (messageDirection === "Inbound" && messageConvId === conversation?.id) {
        // Deduplication: prevent processing same message multiple times
        const messageId = message.id || message.Id || `${messageConvId}-${message.createdAt || message.CreatedAt}`;
        if (lastProcessedMessageIdRef.current === messageId) {
          return;
        }
        lastProcessedMessageIdRef.current = messageId;
        // Inbound message = waiting for operator reply, so use consumer timer
        setOperatorTimerActive(false);
        operatorTimerActiveRef.current = false;
        setLockExpiry(Date.now() + lockDurationMsRef.current);
      }
    };

    window.addEventListener("signalr-new-message-notification", handleNewMessage);

    return () => {
      window.removeEventListener("signalr-new-message-notification", handleNewMessage);
    };
  }, [conversation?.id]);

  // REMOVED: Direct SignalR ReceiveMessage listener
  // Timer reset is now handled ONLY via custom event 'signalr-new-message-notification' above
  // This prevents duplicate timer resets from the same message
  // SignalRProvider is the SINGLE source of truth for SignalR events

  // Handle operator message sent - switch to operator timer (or reset if already on operator timer)
  const handleOperatorMessageSent = useCallback(() => {
    const newExpiry = Date.now() + operatorTimeoutMsRef.current;
    // Switch to operator timer mode
    setOperatorTimerActive(true);
    operatorTimerActiveRef.current = true;
    setLockExpiry(newExpiry);
  }, []);

  // Handle inbound message received - switch to consumer timer (waiting for operator reply)
  const handleInboundMessageReceived = useCallback(() => {
    // Inbound message = consumer sent message, now waiting for operator reply
    // So we switch to consumer timer (operator has X minutes to reply)
    const newLockExpiry = Date.now() + lockDurationMsRef.current;
    setOperatorTimerActive(false);
    operatorTimerActiveRef.current = false;
    setLockExpiry(newLockExpiry);
  }, []);

  // Explicit back handler with loading state - replaces cleanup effect
  const handleBackWithUnlock = useCallback(async () => {
    if (isUnlocking) return; // Prevent double-clicks
    setIsUnlocking(true);
    
    try {
      // Clear timer first
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Await unlock before navigating
      if (conversation?.id) {
        await unlockConversation(conversation.id);
      }
      
      // Now safe to navigate back
      onBack();
    } catch (error) {
      console.error("[MessageReply] ❌ Failed to unlock conversation:", error);
      showNotification("Failed to release conversation. Please try again.", "error");
      setIsUnlocking(false); // Re-enable button on error
    }
  }, [conversation?.id, unlockConversation, onBack, isUnlocking, showNotification]);

  // Cleanup: Only clear timer on unmount (unlock is handled by handleBackWithUnlock)
  useEffect(() => {
    isUnmountingRef.current = false;
    
    return () => {
      isUnmountingRef.current = true;
      // Only clear timer - unlock is handled by handleBackWithUnlock
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [conversation?.id]);

  // Handle browser close/tab close - use sendBeacon to unlock conversation
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (conversation?.id) {
        // Use sendBeacon for reliable delivery even during page unload
        const token = localStorage.getItem("token");
        const unlockUrl = `/api/conversations/${conversation.id}/unlock`;
        
        // sendBeacon with JSON body
        const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
        
        // Note: sendBeacon doesn't support custom headers, so we use a query param for auth
        // Backend should support this alternative auth method for unlock endpoint
        navigator.sendBeacon(`${unlockUrl}?token=${encodeURIComponent(token || '')}`);
      }
    };

    // Also handle visibility change (tab switching, minimizing)
    const handleVisibilityChange = () => {
      // Visibility change handled - server will unlock on disconnect if needed
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [conversation?.id]);

  useEffect(() => {
    if (hasFetched || !conversation?.id) return;

    const fetchAll = async () => {
      // Fetch messages using pagination (load last 20 messages initially)
      const result = await getPaginatedMessages(conversation.id, 0, 20);
      if (result?.data) {
        const { messages: messagesData, totalCount, hasMore } = result.data;
        setAllMessages(Array.isArray(messagesData) ? messagesData : []);
        setInitialPaginationInfo({ totalCount, hasMore });
      } else {
        // Fallback to old method if paginated endpoint fails
        const fallbackResult = await getConversationMessages(conversation.id);
        const messagesData = fallbackResult?.data || fallbackResult;
        setAllMessages(Array.isArray(messagesData) ? messagesData : []);
      }

      // Fetch consumer details
      let consumer = null;
      if (conversation.consumerId) {
        const consumerResult = await getConsumerById(conversation.consumerId);
        consumer =
          consumerResult && consumerResult.data ? consumerResult.data : null;
        // Always fetch credit balance from API
        if (consumer && consumer.id) {
          const creditResult = await getCreditBalance(consumer.id);
          let credit =
            creditResult && creditResult.data !== undefined
              ? creditResult.data
              : 0;
          consumer = { ...consumer, creditBalance: credit };
        }
      }
      setConsumerDetails(consumer);

      // Mark all messages as delivered
      if (conversation?.id) {
        await getConversationMessages.markAllMessagesAsDelivered?.(
          conversation.id
        );
      }
    };

    fetchAll()
      .then(() => setHasFetched(true))
      .catch((error) => {
        console.error("Failed to fetch data:", error);
        setHasFetched(true);
      });
  }, [
    conversation?.id,
    conversation?.consumerId,
    getConversationMessages,
    getConsumerById,
    getCreditBalance,
    hasFetched,
  ]);

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!conversation) {
    return null;
  }

  // Show loading overlay while fetching consumer details and messages
  if (!hasFetched) {
    return (
      <Box 
        sx={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          height: "calc(100vh - 140px)",
          flexDirection: "column",
          gap: 2
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            animation: "spin 1s linear infinite",
            "@keyframes spin": {
              "0%": { transform: "rotate(0deg)" },
              "100%": { transform: "rotate(360deg)" },
            },
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              border: "4px solid #f3f3f3",
              borderTop: "4px solid var(--color-primary)",
              borderRadius: "50%",
            }}
          />
        </Box>
        <Typography variant="h6" sx={{ color: "var(--color-primary)" }}>
          Loading conversation...
        </Typography>
      </Box>
    );
  }

  // Handle timeout modal confirmation
  const handleTimeoutModalClose = () => {
    setShowTimeoutModal(false);
    onBack();
  };

  return (
    <div style={{ padding: "0px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ color: "var(--color-primary)", fontWeight: 700 }}
          >
            MESSAGES
          </Typography>
        </div>
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          {/* Timer Display */}
          {timeRemaining !== null && (
            <Tooltip
              title={operatorTimerActive 
                ? "Outbound: Time remaining for you to reply to the consumer" 
                : "Inbound: Waiting time for consumer's response"}
              arrow
              placement="bottom"
            >
              <Alert
                icon={operatorTimerActive ? <ArrowCircleUpIcon /> : <ArrowCircleDownIcon />}
                severity={timeRemaining < 60000 ? "warning" : "info"}
                sx={{
                  py: "6px",
                  px: 2,
                  minHeight: "36.5px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "help",
                  "& .MuiAlert-message": {
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    padding: 0.3,
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiAlert-icon": {
                    padding: 0.3,
                    marginRight: 1,
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                {operatorTimerActive ? "Outbound" : "Inbound"}: {formatTime(timeRemaining)}
              </Alert>
            </Tooltip>
          )}
          <Badge 
            badgeContent={localUnreadConversationCount} 
            color="error"
            max={99}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.75rem',
                fontWeight: 'bold',
                minWidth: 20,
                height: 20,
                borderRadius: '10px',
              }
            }}
          >
            <Button
              variant="outlined"
              onClick={handleBackWithUnlock}
              disabled={isUnlocking}
              sx={{
                color: "#fff",
                borderColor: "#635f5fff",
                backgroundColor: "#635f5fff",
                "&:hover": { backgroundColor: "#333", borderColor: "#333" },
                "&:disabled": { backgroundColor: "#999", borderColor: "#999", color: "#ccc" },
              }}
            >
              {isUnlocking ? "Releasing..." : "Back to Messages"}
            </Button>
          </Badge>
        </div>
      </div>

      {/* Main Content - 3 Column Layout */}
      <Paper sx={{ 
        height: "calc(100vh - 140px)", 
        display: "flex",
        // Blur and disable when session timed out
        filter: showTimeoutModal ? 'blur(8px)' : 'none',
        pointerEvents: showTimeoutModal ? 'none' : 'auto',
        transition: 'filter 0.3s ease',
      }}>
        {/* Consumer Details Panel */}
        <Box
          sx={{
            width: "400px",
            borderRight: "1px solid #e0e0e0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ConsumerDetails consumer={consumerDetails} />
        </Box>

        {/* Chat Interface Panel */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ChatInterface
            conversationId={conversation.id}
            consumer={consumerDetails}
            allMessages={allMessages}
            initialPaginationInfo={initialPaginationInfo}
            currentOperatorId={user.id}
            onOperatorMessageSent={handleOperatorMessageSent}
            onInboundMessageReceived={handleInboundMessageReceived}
          />
        </Box>
      </Paper>

      {/* Session Timeout Modal */}
      <Dialog
        open={showTimeoutModal}
        onClose={() => {}} // Prevent closing by clicking outside
        aria-labelledby="timeout-dialog-title"
        aria-describedby="timeout-dialog-description"
        disableEscapeKeyDown // Prevent closing with Escape key
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 400,
            textAlign: 'center',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.85)', // Dark backdrop to hide chat content
          }
        }}
      >
        <DialogTitle 
          id="timeout-dialog-title"
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 1,
            color: 'warning.main',
            pt: 3
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" component="span" fontWeight={600}>
            Conversation Timeout
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography 
            id="timeout-dialog-description" 
            sx={{ mt: 1, color: 'text.secondary' }}
          >
            Your session for this conversation has expired due to inactivity. 
            The conversation has been unlocked and is now available for other operators.
          </Typography>
          <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: '0.9rem' }}>
            You will be redirected back to the message list.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={handleTimeoutModalClose} 
            variant="contained"
            color="primary"
            sx={{ minWidth: 120 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MessageReply;
